import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ClipboardList } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, formatTimeInTimezone, shiftStatus, PAGE, SHIFT } from '@kioskgaming/ui';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import { fetchShiftReports, fetchShopCashiers } from '../services/api';
import type { CashierShift, ShopCashier } from '../types';

function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shiftStatusClass(status: string): string {
  if (status === 'completed') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'completed_with_discrepancy') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (status === 'pending_reconciliation' || status === 'pending_manager_review') {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

export default function ShiftReportsPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const [reports, setReports] = useState<CashierShift[]>([]);
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toDateInputValue(d);
  });
  const [to, setTo] = useState(() => toDateInputValue(new Date()));

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const cashierNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cashiers) map.set(c.id, c.name);
    return map;
  }, [cashiers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, cashierRes] = await Promise.all([
        fetchShiftReports({ from, to }),
        fetchShopCashiers(),
      ]);
      if (!reportRes.success) throw new Error(reportRes.message || 'Failed to load reports');
      setReports(reportRes.data || []);
      if (cashierRes.success) setCashiers(cashierRes.data || []);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const metrics = useMemo(() => {
    const completed = reports.filter((r) => r.status === 'completed').length;
    const withDiscrepancy = reports.filter((r) => r.status === 'completed_with_discrepancy').length;
    const pending = reports.filter(
      (r) => r.status === 'pending_reconciliation' || r.status === 'pending_manager_review'
    ).length;
    return { total: reports.length, completed, withDiscrepancy, pending };
  }, [reports]);

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.shiftReports}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Completed and pending reconciliation shifts for audit and review.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-stone-600">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Total</div>
            <div className="mt-1 text-2xl font-bold text-stone-900">{metrics.total}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">Completed</div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">{metrics.completed}</div>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-rose-700">With discrepancy</div>
            <div className="mt-1 text-2xl font-bold text-rose-900">{metrics.withDiscrepancy}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-amber-700">Pending review</div>
            <div className="mt-1 text-2xl font-bold text-amber-900">{metrics.pending}</div>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-stone-200 bg-white">
          {loading ? (
            <div className="px-4 py-8 text-sm text-stone-600">Loading…</div>
          ) : reports.length ? (
            <div className="divide-y divide-stone-100">
              {reports.map((shift) => {
                const needsReconcile =
                  shift.status === 'pending_reconciliation' || shift.status === 'pending_manager_review';
                return (
                  <div key={shift.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/shifts/${encodeURIComponent(shift.id)}`)}
                      className="min-w-0 flex-1 text-left hover:opacity-80"
                    >
                      <div className="truncate text-sm font-semibold text-stone-900">
                        {cashierNameById.get(shift.cashierId) || 'Cashier'}
                      </div>
                      <div className="mt-0.5 text-xs text-stone-500">
                        {shift.shiftDate || '—'} · {formatTimeInTimezone(shift.scheduledStartAt, timezone)}–{formatTimeInTimezone(shift.scheduledEndAt, timezone)}
                      </div>
                      <div className="mt-0.5 text-xs text-stone-600">
                        Opening: {shift.openingCashBalance ?? '—'} · Counted: {shift.closingCashCounted ?? '—'} ·
                        Discrepancy: {shift.discrepancyAmount ?? '—'}
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${shiftStatusClass(shift.status)}`}
                      >
                        {shiftStatus(shift.status).label}
                      </span>
                      {needsReconcile ? (
                        <button
                          type="button"
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          onClick={() => navigate(`/reconciliation/${encodeURIComponent(shift.id)}`)}
                        >
                          {SHIFT.reconcileShift}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                          onClick={() => navigate(`/reconciliation/${encodeURIComponent(shift.id)}`)}
                        >
                          View reconciliation
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <PortalEmptyState
              icon={<ClipboardList className="mx-auto h-12 w-12 text-stone-400" strokeWidth={1.5} />}
              title="No shift reports"
              description="Reports will appear for completed or pending-reconciliation shifts in the selected date range."
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
